import {
  type CreateUpdateOrderDTO,
  CustomerType,
  type OrderDTO,
  type SveaOrder,
  type SveaOrderRow
} from 'ecosystem'
import {
  convertMinorUnitToValue,
  createMinorUnit,
  getTax,
  getVatRateByCountryCode,
  roundNumber,
  sumArrayOfNumbers
} from 'shared-utils'
import { createError } from '../../../../utils/fetching'
import { generateSveaAddresses } from '../utils'
import { type CompleteStorefrontOrderProps } from './types'
import {
  generateBasePayload,
  generatePayloadFromDefaultDetails,
  generatePayloadFromIngrid,
  generatePayloadFromNshift,
  generatePayloadFromPostNord
} from './utils'

const generateBasePayloadFromSvea = (
  oldOrder: OrderDTO,
  sveaOrder: SveaOrder
): CreateUpdateOrderDTO => {
  const {
    billingAddress,
    shippingAddress,
    customerInfo: { email, firstName, lastName }
  } = generateSveaAddresses(sveaOrder)
  const sveaItems = sveaOrder.Cart.Items

  const orderDiscount = sveaItems.reduce((sum, item) => {
    return item.DiscountAmount ? sum + item.DiscountAmount : sum
  }, 0)

  const shippingCost = sveaItems.reduce((sum, item) => {
    return item.ArticleNumber === 'delivery' ? sum + item.UnitPrice : sum
  }, 0)

  const getItemTotalPriceWithDiscount = (item: SveaOrderRow) =>
    item.UnitPrice * convertMinorUnitToValue(item.Quantity) - (item.DiscountAmount ?? 0)

  const orderAmount = convertMinorUnitToValue(
    sumArrayOfNumbers(sveaItems.map(getItemTotalPriceWithDiscount))
  )

  const orderTaxAmount = roundNumber(
    convertMinorUnitToValue(
      sumArrayOfNumbers(
        sveaItems.map((item) =>
          getTax(
            getItemTotalPriceWithDiscount(item),
            item.VatPercent ?? createMinorUnit(getVatRateByCountryCode(sveaOrder?.CountryCode)),
            true
          )
        )
      )
    )
  )

  return {
    ...generateBasePayload(oldOrder),
    billingAddress,
    shippingAddress,
    currency: sveaOrder.Currency,
    customer: {
      email,
      firstName,
      lastName,
      type: CustomerType.CONSUMER
    },
    orderAmount,
    orderTaxAmount,
    orderDiscount: roundNumber(convertMinorUnitToValue(orderDiscount)),
    shippingCost: convertMinorUnitToValue(shippingCost),

    paymentId: String(sveaOrder.OrderId),
    paymentProvider: 'SVEA'
  }
}

export const sveaPaymentProvider = ({
  order: oldOrder,
  paymentProviderInfo,
  shippingProviderInfo
}: CompleteStorefrontOrderProps): CreateUpdateOrderDTO => {
  if (!paymentProviderInfo.providerResponse?.sveaResponse) {
    throw createError('Not found SVEA order')
  }
  const shippingProviderName = shippingProviderInfo?.providerName ?? 'DEFAULT'

  let payload = generateBasePayloadFromSvea(
    oldOrder,
    paymentProviderInfo.providerResponse?.sveaResponse
  )

  switch (shippingProviderName) {
    case 'DEFAULT':
      // Carry the FE-supplied delivery selection (pickup vs delivery,
      // shipping address, shipping cost, raw `defaultDetails`) forward
      // from the persisted order — no carrier response to derive it from.
      payload = generatePayloadFromDefaultDetails(payload, oldOrder)
      break
    case 'POSTNORD':
      payload = generatePayloadFromPostNord(payload, shippingProviderInfo?.postnordResponse)
      break
    case 'INGRID':
      payload = generatePayloadFromIngrid(payload, shippingProviderInfo?.ingridResponse)
      break
    case 'NSHIFT':
      payload = generatePayloadFromNshift(payload, shippingProviderInfo?.nshiftResponse)
  }

  return payload
}
